// src/pages/qc/PageRdEquipment.jsx
import { useState, useEffect, useMemo } from "react";
import * as api from "../../apiService";
import { fmtDate } from "../../utils/dateUtils";
import { EquipmentCreateModal } from "./EquipmentCreateModal";
import { EquipmentDetailModal } from "./EquipmentDetailModal";
import { useToast } from "../../components/Toast";

const CAL_STATUS = {
  OK:               { bg: "#d1fae5", color: "#065f46", label: "OK" },
  DUE_SOON:         { bg: "#fef3c7", color: "#92400e", label: "Due Soon" },
  OVERDUE:          { bg: "#fee2e2", color: "#991b1b", label: "Overdue" },
  NEVER_CALIBRATED: { bg: "#e0e7ff", color: "#3730a3", label: "Never Calibrated" },
  NOT_REQUIRED:     { bg: "#f3f4f6", color: "#6b7280", label: "Not Required" },
};

function CalStatusBadge({ status }) {
  const s = CAL_STATUS[status] ?? CAL_STATUS.NOT_REQUIRED;
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 4,
      padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
      {s.label}
    </span>
  );
}

export function PageRdEquipment({ myPerms }) {
  const showToast = useToast();
  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [search,        setSearch]        = useState("");
  const [calFilter,     setCalFilter]     = useState("ALL");
  const [locationFilter, setLocationFilter] = useState("ALL");
  const [createModal,   setCreateModal]   = useState(false);
  const [detailRow,   setDetailRow]   = useState(null);

  const canEdit   = myPerms?.includes("QC_EDIT") || myPerms?.includes("RND_EDIT");
  const canDelete = myPerms?.includes("USER_MANAGE");

  function load() {
    setLoading(true);
    api.fetchEquipment()
      .then(setRows)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []); // eslint-disable-line

  const locations = useMemo(() => {
    const s = new Set(rows.map(r => r.location).filter(Boolean));
    return [...s].sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter(r => {
      const matchCal      = calFilter      === "ALL" || r.calibrationStatus === calFilter;
      const matchLocation = locationFilter === "ALL" || r.location === locationFilter;
      const matchQ        = !q ||
        (r.equipmentNo || "").toLowerCase().includes(q) ||
        (r.name        || "").toLowerCase().includes(q) ||
        (r.maker       || "").toLowerCase().includes(q) ||
        (r.location    || "").toLowerCase().includes(q);
      return matchCal && matchLocation && matchQ;
    });
  }, [rows, search, calFilter, locationFilter]);

  async function handleCreate(body) {
    const created = await api.createEquipment(body);
    setRows(old => [...old, created]);
    setCreateModal(false);
    showToast("Equipment added");
  }

  async function handleUpdate(id, body) {
    const updated = await api.updateEquipment(id, body);
    setRows(old => old.map(r => r.id === updated.id ? updated : r));
    setDetailRow(updated);
    showToast("Equipment updated");
    return updated;
  }

  async function handleDelete(id) {
    if (!window.confirm("Remove this equipment? This cannot be undone.")) return;
    await api.deleteEquipment(id);
    setRows(old => old.filter(r => r.id !== id));
    setDetailRow(null);
    showToast("Equipment removed", "info");
  }

  const overdueCount  = rows.filter(r => r.calibrationStatus === "OVERDUE").length;
  const dueSoonCount  = rows.filter(r => r.calibrationStatus === "DUE_SOON").length;

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Equipment List</div>
          <div className="page-subtitle">Research &amp; Development · Instruments, tools and calibration schedule</div>
        </div>
        {canEdit && (
          <div className="page-header-right">
            <button className="btn btn-primary" onClick={() => setCreateModal(true)}>+ Add Equipment</button>
          </div>
        )}
      </div>

      {(overdueCount > 0 || dueSoonCount > 0) && (
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          {overdueCount > 0 && (
            <div style={{ background: "#fee2e2", color: "#991b1b", borderRadius: 6,
              padding: "6px 12px", fontSize: 13, fontWeight: 600 }}>
              ⚠ {overdueCount} overdue calibration{overdueCount !== 1 ? "s" : ""}
            </div>
          )}
          {dueSoonCount > 0 && (
            <div style={{ background: "#fef3c7", color: "#92400e", borderRadius: 6,
              padding: "6px 12px", fontSize: 13, fontWeight: 600 }}>
              ⏰ {dueSoonCount} calibration{dueSoonCount !== 1 ? "s" : ""} due soon
            </div>
          )}
        </div>
      )}

      <div className="action-bar" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <input className="form-input" placeholder="Search equipment no, name, maker, location…"
          value={search} onChange={e => setSearch(e.target.value)} style={{ width: 280 }} />
        <select className="form-select" value={locationFilter}
          onChange={e => setLocationFilter(e.target.value)} style={{ width: 160 }}>
          <option value="ALL">All Locations</option>
          {locations.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <select className="form-select" value={calFilter}
          onChange={e => setCalFilter(e.target.value)} style={{ width: 190 }}>
          <option value="ALL">All Cal. Statuses</option>
          <option value="OK">OK</option>
          <option value="DUE_SOON">Due Soon</option>
          <option value="OVERDUE">Overdue</option>
          <option value="NEVER_CALIBRATED">Never Calibrated</option>
          <option value="NOT_REQUIRED">Not Required</option>
        </select>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "#6b7280" }}>
          {filtered.length} of {rows.length} equipment
        </span>
      </div>

      {loading && <div style={{ color: "#aaa", fontSize: 12 }}>Loading…</div>}
      {error   && <div className="alert-warn">Error: {error}</div>}

      {!loading && !error && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Equip. No</th>
                <th>Name</th>
                <th>Location</th>
                <th>Qty</th>
                <th>Maker</th>
                <th>Range</th>
                <th>Least Count</th>
                <th>Frequency</th>
                <th>Type</th>
                <th>Next Due</th>
                <th>Calibration</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={11}
                  style={{ textAlign: "center", color: "#aaa", padding: 24 }}>
                  No equipment found
                </td></tr>
              ) : filtered.map(row => (
                <tr key={row.id} style={{ cursor: "pointer" }}
                  onClick={() => setDetailRow(row)}>
                  <td style={{ fontFamily: "monospace", fontWeight: 600 }}>{row.equipmentNo || "—"}</td>
                  <td className="fw-600">{row.name}</td>
                  <td style={{ fontSize: 12 }}>{row.location || "—"}</td>
                  <td>{row.quantity}</td>
                  <td>{row.maker || "—"}</td>
                  <td>{row.range || "—"}</td>
                  <td>{row.leastCount || "—"}</td>
                  <td style={{ fontSize: 12 }}>{row.calibrationFrequency || "—"}</td>
                  <td style={{ fontSize: 12 }}>{row.calibrationType || "—"}</td>
                  <td style={{ fontSize: 12 }}>{row.nextDueDate ? fmtDate(row.nextDueDate) : "—"}</td>
                  <td><CalStatusBadge status={row.calibrationStatus} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {createModal && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setCreateModal(false); }}>
          <EquipmentCreateModal
            onSave={handleCreate}
            onCancel={() => setCreateModal(false)}
          />
        </div>
      )}

      {detailRow && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setDetailRow(null); }}>
          <EquipmentDetailModal
            equipment={detailRow}
            canEdit={canEdit}
            canDelete={canDelete}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onEquipmentUpdated={fresh => setRows(old => old.map(r => r.id === fresh.id ? fresh : r))}
            onClose={() => setDetailRow(null)}
          />
        </div>
      )}
    </>
  );
}
